import { useState } from 'react';
import { Launch as LaunchIcon } from '@mui/icons-material';
import Modal from '@mui/material/Modal';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { FlatConditionResult } from '@tech-insights/workflows-sdk';
import DOMPurify from 'dompurify';
import ReactJson from 'react-json-view';

export type FactValueDisplayType = {
  check: FlatConditionResult;
};

export const FactValueDisplay = ({ check }: Readonly<FactValueDisplayType>) => {
  const [modalOpen, setModalOpen] = useState(false);
  const theme = useTheme();
  const factValue = check?.factValue;

  const isComplexValue = (item: unknown) => {
    return (
      item !== null &&
      typeof item === 'object' &&
      !Array.isArray(item) &&
      !(item instanceof Date)
    );
  };

  const formatSimpleValue = (item: unknown) => {
    if (item === null || item === undefined) {
      return 'null';
    }
    if (typeof item === 'string') {
      return item;
    }
    if (typeof item === 'boolean') {
      return item.toString();
    }
    if (typeof item === 'number') {
      return item.toString();
    }
    if (item instanceof Date) {
      return item.toLocaleString();
    }
    if (Array.isArray(item)) {
      return JSON.stringify(item);
    }
    return JSON.stringify(item);
  };

  const handleModalClose = () => setModalOpen(false);
  const handleModalOpen = () => setModalOpen(true);

  if (factValue !== undefined && isComplexValue(factValue)) {
    return (
      <>
        <Typography
          sx={{
            alignSelf: 'center',
            marginRight: '8px',
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' },
          }}
          onClick={handleModalOpen}
        >
          View fact value
          <IconButton size="small">
            <LaunchIcon fontSize="small" />
          </IconButton>
        </Typography>

        <Modal
          open={modalOpen}
          onClose={handleModalClose}
          aria-labelledby="fact-value-modal"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '60%',
              maxHeight: '70%',
              bgcolor: 'background.paper',
              boxShadow: 24,
              p: 4,
              overflow: 'auto',
            }}
          >
            <Typography variant="h6" component="h2" gutterBottom>
              Fact Value
            </Typography>
            <Box
              sx={{
                backgroundColor: theme.palette.background.default,
                overflow: 'auto',
              }}
            >
              <ReactJson
                src={factValue ?? {}}
                theme={
                  theme.palette.mode === 'dark'
                    ? 'shapeshifter'
                    : 'shapeshifter:inverted'
                }
                displayDataTypes={false}
                displayObjectSize={false}
                enableClipboard
                collapsed={false}
                name={false}
              />
            </Box>
          </Box>
        </Modal>
      </>
    );
  }

  return <>{DOMPurify.sanitize(String(formatSimpleValue(factValue)))}</>;
};
